import { looseIndex } from '#library/object.library.js';
import { sym as lib, $Target, $Discover, $Extensible, $Inspect, $LogConfig, $Registry, $Register as $LibRegister, $SerializerRegistry, $Identity } from '#library/symbol.library.js';
export { $Target, $Discover, $Extensible, $Inspect, $LogConfig, $Registry, $LibRegister, $SerializerRegistry, $Identity };
/**
 * Checks if the given value is a valid Tempo instance.
 * Uses brand checking via the $Identity symbol.
 *
 * @param tempo - The value to check
 * @returns True if the value is a Tempo instance, false otherwise
 * @example
 * ```ts
 * if (isTempo(value)) {
 *   // value is narrowed to Tempo type
 * }
 * ```
 */
export const isTempo = (tempo) => tempo?.[sym.$Identity] === true;
/**
 * Centralized registry for all Tempo-specific Global Symbols.
 * These symbols utilize Symbol.for() to ensure consistency across module boundaries.
 */
export const TermError = Symbol.for('magmacomputing/tempo/termError');
/**
 * Well-Known Symbols for Term Lifecycle Protocol.
 * Utilizing Symbol.for() to guarantee consistency across module boundaries and dual package instances.
 */
export const TermHook = {
    parse: Symbol.for('magmacomputing/tempo/term/parse'),
    ordinal: Symbol.for('magmacomputing/tempo/term/ordinal'),
    step: Symbol.for('magmacomputing/tempo/term/step'),
    diff: Symbol.for('magmacomputing/tempo/term/diff'),
    bound: Symbol.for('magmacomputing/tempo/term/bound'),
    format: Symbol.for('magmacomputing/tempo/term/format')
};
/** @internal unique symbols for critical internal accessors */
/** key for Global Discovery of Tempo configuration */ export const $Tempo = Symbol.for('$Tempo');
/** key for Reactive Plugin Registration */ export const $Register = Symbol.for('magmacomputing/tempo/register');
/** key for Internal Interpreter Service */ export const $Interpreter = Symbol.for('magmacomputing/tempo/interpreter');
/** key for Master Guard */ export const $guard = Symbol.for('magmacomputing/tempo/guard');
/** internal key for signaling pre-errored state */ export const $errored = Symbol.for('magmacomputing/tempo/errored');
/** internal key for accessing private instance state */ export const $Internal = Symbol.for('magmacomputing/tempo/internal');
/** hardened globalThis bridge key for the TempoRuntime */ export const $Bridge = Symbol.for('magmacomputing/tempo/runtime');
/** cross-bundle brand check for TempoRuntime */ export const $RuntimeBrand = Symbol.for('magmacomputing/tempo/runtime/brand');
/** branding for explicit PropertyDescriptors */ export const $Descriptor = Symbol.for('magmacomputing/tempo/descriptor');
/** key for Plugin Discrimination */ export const $PluginType = Symbol.for('magmacomputing/tempo/pluginType');
/** @internal static config helper */ export const $setConfig = Symbol.for('magmacomputing/tempo/setConfig');
/** @internal static discovery helper */ export const $setDiscovery = Symbol.for('magmacomputing/tempo/setDiscovery');
/** @internal static event builder */ export const $setEvents = Symbol.for('magmacomputing/tempo/setEvents');
/** @internal static period builder */ export const $setPeriods = Symbol.for('magmacomputing/tempo/setPeriods');
/** @internal static alias builder */ export const $setAliases = Symbol.for('magmacomputing/tempo/setAliases');
/** @internal static guard builder */ export const $buildGuard = Symbol.for('magmacomputing/tempo/buildGuard');
/** @internal static base class marker */ export const $IsBase = Symbol.for('magmacomputing/tempo/isBase');
/** @internal Tempo Symbol Registry (Local Keys) */
const local = {
    $Tempo, $Register, $Interpreter, $guard, $errored,
    $Internal, $Bridge, $RuntimeBrand, $Descriptor, $setConfig, $setDiscovery,
    $setEvents, $setPeriods, $setAliases, $buildGuard, $IsBase, $PluginType
};
/** @internal Unified Symbol Registry (Inherits from #library via Prototype Chain) */
export const sym = Object.assign(Object.create(lib), local);
/** @internal Tempo Token registry */
export const Token = looseIndex()({
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ Snippet Symbols
    /** year */ yy: Symbol('yy'),
    /** ISO yearOfWeek */ yw: Symbol('yw'),
    /** month */ mm: Symbol('mm'),
    /** week */ ww: Symbol('ww'),
    /** day */ dd: Symbol('dd'),
    /** hour */ hh: Symbol('hh'),
    /** minute */ mi: Symbol('mi'),
    /** second */ ss: Symbol('ss'),
    /** fraction */ ff: Symbol('ff'),
    /** ordinal suffix */ ord: Symbol('ord'),
    /** meridiem */ mer: Symbol('mer'),
    /** short weekday name */ www: Symbol('www'),
    /** relative-suffix */ afx: Symbol('afx'),
    /** time-suffix */ sfx: Symbol('sfx'),
    /** time unit */ unt: Symbol('unt'),
    /** separator */ sep: Symbol('sep'),
    /** modifier */ mod: Symbol('mod'),
    /** generic number */ nbr: Symbol('nbr'),
    /** Tempo slick shorthand */ slk: Symbol('slk'),
    /** Tempo event */ evt: Symbol('evt'),
    /** Tempo period */ per: Symbol('per'),
    /** time zone offset or identifier */ tz: Symbol('tz'),
    /**
     * time zone offset
     * @deprecated Deprecated in v4.2.0; to be removed in v5.0.0. Use `Token.tz` instead.
     */
    tzd: Symbol('tzd'),
    /** historical era */ era: Symbol('era'),
    /** ordinal index (1st, 2nd, 3rd, last, etc) */ nth: Symbol('nth'),
    /** Roman month (I-XII) */ Rm: Symbol('romanMonth'),
    /** Roman year (e.g. MMXXVI) */ Ry: Symbol('romanYear'),
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ Layout Symbols
    /** date */ dt: Symbol('date'),
    /** time */ tm: Symbol('time'),
    /** compact time (hhmiss) */ hms: Symbol('hourMinuteSecond'),
    /** compact day-month-year (ddmmyy) */ dmy6: Symbol('dayMonthYearShort'),
    /** compact month-day-year (mmddyy) */ mdy6: Symbol('monthDayYearShort'),
    /** compact year-month-day (yymmdd) */ ymd6: Symbol('yearMonthDayShort'),
    /** date and time */ dtm: Symbol('dateTime'),
    /** time and date */ tmd: Symbol('timeDate'),
    /** day-month-year */ dmy: Symbol('dayMonthYear'),
    /** month-day-year */ mdy: Symbol('monthDayYear'),
    /** year-month-day */ ymd: Symbol('yearMonthDay'),
    /** day of month offset */ off: Symbol('offset'),
    /** weekDay */ wkd: Symbol('weekDay'),
    /** relative offset (years, days, hours, etc) */ rel: Symbol('relativeOffset'),
    /** timezone/calendar brackets */ brk: Symbol('brackets'),
    /** year and era */ ye: Symbol('yearEra'),
    /** era and year (leading) */ ey: Symbol('eraYearLeading'),
    /** ordinal weekday layout */ ordWkd: Symbol('ordinalWeekday'),
    /** ordinal unit/day layout */ ordUnt: Symbol('ordinalUnit'),
    /** day-RomanMonth-year (e.g. 14.X.2026) */ dRy: Symbol('dayRomanMonthYear'),
    /** archival / inscription Roman year (e.g. MMXXVI) */ Ryy: Symbol('romanYearOnly'),
});
